<script setup lang="ts">
import { computed, ref } from "vue";
import { round } from "lodash-es";

// @ts-ignore we dont need types for this
import hsl2rgb from "hsl-rgb";

import FormSwitch from "@Forms/FormSwitch.vue";
import { VueECharts as VChart } from "@Helpers/echarts";
import useTranslation from "@Composables/useTranslation";
import { useHandleResize, ResizableChart } from "@Composables/useHandleResize";
import GeneralStats from "@Modules/analytics/DTOs/GeneralStats";
import { ValueChartDataProvider } from "@Modules/analytics/Components/PercentValueChart/ValueChart";
import ChartLayout from "@Modules/analytics/Components/ChartLayout.vue";

//
// Props 
interface Props {
  dataset: ValueChartDataProvider;
  title?: string;
  loading?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  title: "Tree Chart",
  dataset: () => new GeneralStats(),
  loading: false,
});

//
// Setup
const { __ } = useTranslation();

//
// Data
const showPercent = ref<boolean>(false);
const chart = ref<ResizableChart | null>(null);

useHandleResize(chart);

//
// Computed
const chartData = computed(() => props.dataset.getChartData() || []);

//
// Methods

// @ts-ignore legacy chart code
const dec2hex = (n) => {
    n = parseInt(n);
    var c = "ABCDEF";
    var b: string | number = n / 16;
    var r = n % 16;
    b = b - r / 16;
    b = b >= 0 && b <= 9 ? b : c.charAt(b - 10);
    return r >= 0 && r <= 9 ? b + "" + r : b + "" + c.charAt(r - 10);
};

// @ts-ignore legacy chart code
const hsl2color = (h, s, l) => {
    const rgb = hsl2rgb(h, s, l);

    return (
        "#" + dec2hex(rgb[0]) + dec2hex(rgb[1]) + dec2hex(rgb[2])
    );
};

// @ts-ignore legacy chart code
const hsl2brightness = (h, s, l) => {
    const lightness = hsl2rgb(h, s, l);

    return (
        0.2126 * lightness[0] + 0.7152 * lightness[1] + 0.0722 * lightness[2]
    );
};

// @ts-ignore legacy chart code
const addColors = (data) => {
    const hueDelta = Math.floor(360 / data.length);
    let hue = 0;

    for (const i in data) {
        data[i].itemStyle = {
            color: hsl2color(hue, 0.5, 0.4),
            borderColor: hsl2color(hue, 0.5, 0.8),
        };

        if (data[i].children) {
            const lightDelta = 0.6 / data[i].children.length;
            let light = 0.4;

            for (const j in data[i].children) {
                const color = hsl2color(hue, 0.85, light);
                const brightness = hsl2brightness(hue, 0.85, light);

                data[i].children[j].itemStyle = {
                    color: color,
                    borderColor: color,
                };
                data[i].children[j].label = {
                    color: brightness > 120 ? "#000000" : "#FFFFFF",
                };

                light += lightDelta;
            }
        }

        hue += hueDelta;
    }

    return data;
};

const chartOptions = computed(() => {
    // @ts-ignore we need this unused value here so that this object re computes when we click the toggle
    // eslint-disable
    const shouldShowPercent = showPercent.value;
    return {
        title: {
            text: __("chart.title.browsers"),
            left: "center",
            top: "top",
            show: false,
        },
        tooltip: {
            trigger: "item",
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- echarts v4 formatter callback params are untyped
            formatter: (params: any) => {
                const tree = params.treePathInfo;
                const totalCount = tree[0].value;

                if (tree.length === 1) {
                    return "";
                }

                let name = tree[1].name;
                if (tree.length === 3) {
                    name += " <b>" + tree[2].source + "</b>";
                }

                //    const name = params.data.source;
                const template =
                    ":name:<br>" + __("chart.clicks") + ": :value: (:percent:%)";
                return template
                    .replace(":name:", params.name)
                    .replace(":value:", params.data.value)
                    .replace(":percent:", "" + round((params.value / totalCount) * 100, 2));
            },
        },
        toolbox: {
            show: false,
            feature: {
                saveAsImage: {
                    show: true,
                    title: __("chart.save_as_image"),
                },
            },
        },
        series: [
            {
                type: "treemap",
                top: "0%",
                bottom: "0%",
                left: "0%",
                right: "0%",
                height: "100%",
                width: "100%",
                upperLabel: {
                    show: true,
                    height: 36,
                    borderColor: "auto",
                    fontWeight: "500",
                    fontSize: 16,
                    color: "#000",
                    padding: [0, 6, 0, 6],
                    emphasis: { color: "#222" },
                    fontFamily: "'Montserrat'",
                    rich: {
                        /* fontF: {
                          color: "#000",
                          height: 36,
                          fontSize: 18,
                          fontWeight: 500,
                          fontFamily: "'Font Awesome 6 Brands'",
                          padding: [0, 6, 0, 3],
                          emphasis: {
                            color: "#222",
                          },
                        }, */
                        fontM: {
                            color: "#000",
                            height: 36,
                            fontSize: 16,
                            fontWeight: 500,
                            fontFamily: "'Montserrat'",
                            padding: [0, 6, 0, 3],
                            emphasis: {
                                color: "#222",
                            },
                        },
                    },
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- echarts v4 formatter callback params are untyped
                    formatter: (params: { name: string, value: number, treePathInfo: any }) => {
                        const total = params.treePathInfo[0].value;
                        const percent = round((params.value / total) * 100, 0);
                        const dataType = showPercent.value ? "percentage" : "click";
                        const finalName =
                            dataType == "click"
                                ? `${params.value} click(s)`
                                : `${percent}%`;

                        // not 100 % sure what this does but it does not appear to be a vue 2 filter
                        return `{fontM|${params.name} | ${finalName}}`;
                    },
                },
                roam: false,
                breadcrumb: false,
                nodeClick: false,
                label: { fontWeight: "bold", fontSize: 14 },
                levels: [
                    {
                        upperLabel: {
                            show: false,
                        },
                        gapWidth: 4,
                        itemStyle: {
                            borderWidth: 6,
                            gapWidth: 4,
                        },
                        label: {
                            fontSize: 16,
                            fontFamily: "'Montserrat'",
                        },
                        emphasis: {
                            itemStyle: {
                                shadowBlur: 10,
                                shadowOffsetX: 0,
                                shadowColor: "rgba(0, 0, 0, 0.5)",
                            },
                        },
                    },
                    {
                        label: { fontWeight: "bold", fontSize: 14 },
                        emphasis: {
                            itemStyle: {
                                shadowBlur: 10,
                                shadowOffsetX: 0,
                                shadowColor: "rgba(0, 0, 0, 0.5)",
                            },
                        },
                    },
                ],
                data: addColors(chartData.value),
            },
        ],
    };
});

</script>

<template>
  <ChartLayout
    class="tree-stat"
    :is-loading="loading"
  >
    <template #header>
      <div class="tree-stat__header">
        <span class="tree-stat__title">
          {{ title }}
        </span>
        <FormSwitch
          v-model="showPercent"
          accessibility-label="Top Browsers Toggler"
          input-id="switch-browsers"
        />
      </div>
    </template>

    <v-chart
      ref="chart"
      class="tree-stat__chart"
      :options="chartOptions"
    />
  </ChartLayout>
</template>

<style lang="scss" scoped>
.tree-stat {
  display: flex;
  flex-flow: column;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__chart {
    flex: 1;
    width: 100%;
    min-width: 0;
  }
}
</style>
